import Vue from 'vue';
import Component from 'vue-class-component';
import { Watch } from 'vue-property-decorator';

import CatalogSelectReservationDateStep from 'components/Catalog/CatalogSelectReservationDateStep';
import HintMessage from 'components/HintMessage';
import BbForm from 'common/components/BbForm';
import BbField from 'common/components/BbField';
import currency from 'common/filters/currency';

import CatalogReservationConfig from 'assets/graphql/queries/catalog/CatalogReservationConfig.graphql';

Vue.filter('currency', currency);

@Component({
  components: {
    CatalogSelectReservationDateStep,
    HintMessage,
    BbForm,
    BbField,
  },
  props: {
    activityId: {
      type: String,
      required: true,
    },
    tabs: {
      type: Array,
      required: true,
    },
    expirationDate: {
      type: String,
      required: true,
    },
    code: {
      type: [String, Object],
      required: false,
    },
    codeId: {
      type: Number,
      required: false,
    },
    codeExpirationDate: {
      type: String,
      required: false,
    },
    locations: {
      type: Array,
      required: true,
    },
    isReservationBuy: {
      type: Boolean,
      required: false,
      default: false,
    },
    showAddToCartButton: {
      type: Boolean,
      required: false,
      default: false,
    },
    activity: {
      type: Object,
      required: false,
    },
    isComboActivity: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  apollo: {
    reservationConfig: {
      query: CatalogReservationConfig,
      variables() {
        return {
          partneractivity_id: this.activityId,
          location_id: this.location?.id || null,
        };
      },
      skip() {
        // Si está habilitada la venta múltiple, validar ambos
        if (this.isMultipleParticipantsEnabled) {
          return (
            !this.location ||
            this.location === '' ||
            !this.selectedActivityQuantity ||
            this.selectedActivityQuantity === ''
          );
        }
        // Si NO está habilitada, solo validar location
        return !this.location || this.location === '';
      },
      update(data) {
        this.$emit('reservationConfigLoaded', data.reservation_config);
        return data.reservation_config;
      },
    },
  },
})
export default class CatalogSelectReservationLocationStepComponent extends Vue {
  errors = [];
  selectedLocation = '';
  selectedActivityQuantity = '';
  form = {
    date: null,
    appointment: null,
  };

  get isMultipleParticipantsEnabled() {
    return (
      this.activity?.participants_options &&
      Array.isArray(this.activity.participants_options) &&
      this.activity.participants_options.length > 1 &&
      !this.codeId
    );
  }

  get participantsOptions() {
    if (!this.isMultipleParticipantsEnabled) return [];
    return this.activity.participants_options.map(participantOption => ({
      label: participantOption,
      value: participantOption / this.activity.participants,
    }));
  }

  get shouldShowDateStep() {
    if (this.isMultipleParticipantsEnabled) {
      return (
        this.selectedActivityQuantity && this.location && this.reservationConfig
      );
    }
    return this.location && this.reservationConfig;
  }

  get location() {
    // Si no hay selección manual y solo hay una ubicación, devolverla automáticamente
    if (
      !this.selectedLocation &&
      this.locations &&
      this.locations.length === 1
    ) {
      return this.locations[0].location;
    }
    return this.selectedLocation;
  }

  set location(value) {
    this.selectedLocation = value;
  }

  async refetchReservationData() {
    const shouldRefetch = this.isMultipleParticipantsEnabled
      ? this.location && this.selectedActivityQuantity
      : this.location;

    if (shouldRefetch) {
      await this.$apollo.queries.reservationConfig.refetch();
    }
  }

  @Watch('location')
  async onLocationChange() {
    await this.refetchReservationData();
  }

  @Watch('selectedActivityQuantity')
  async onActivityQuantityChange() {
    // Resetear appointment para que se pueda auto-seleccionar con la nueva cantidad
    this.form.appointment = null;

    await this.refetchReservationData();
    // Emitir evento para que el padre actualice el header
    this.$emit('activityQuantityChanged', this.selectedActivityQuantity);
  }

  @Watch('showAddToCartButton')
  onShowAddToCartButtonChange(newValue) {
    // Cuando se marca el checkbox "No quiero elegir fecha en este momento"
    // reiniciar la cantidad de actividades
    if (newValue && this.isMultipleParticipantsEnabled) {
      this.selectedActivityQuantity = '';
      // También emitir el cambio al padre para actualizar el header
      this.$emit('activityQuantityChanged', null);
    }
  }

  submitForm() {
    const form = {
      ...this.form,
      location: this.location,
    };

    // Solo agregar activityQuantity si está habilitada la venta múltiple
    if (this.isMultipleParticipantsEnabled && this.selectedActivityQuantity) {
      form.activitysQuantity = this.selectedActivityQuantity;
    }

    this.$emit('selectDateCompleted', form);
  }

  isLoading() {
    this.$emit('isLoading', !this.location);
  }

  addToCart() {
    const cartData = {};

    // Solo agregar activityQuantity si está habilitada la venta múltiple
    if (this.isMultipleParticipantsEnabled && this.selectedActivityQuantity) {
      cartData.activitysQuantity = this.selectedActivityQuantity;
    }

    this.$emit('addToCart', cartData);
  }

  get formIsNotCompleted() {
    return (
      this.form.date === null ||
      !this.location ||
      this.form.appointment === null
    );
  }

  get canContinue() {
    return !Object.values(this.form).some(value => !value) && this.location;
  }

  get totalPrice() {
    // Si está habilitada la venta múltiple y hay una cantidad seleccionada
    if (this.isMultipleParticipantsEnabled && this.selectedActivityQuantity) {
      // selectedActivityQuantity ya contiene la cantidad de actividades
      // (participantOption / activity.participants)
      return this.activity.price * this.selectedActivityQuantity;
    }
    // Si no, retornar el precio normal
    return this.activity.price;
  }
}
